import React from "react";
import { NavLink, useLocation } from "react-router-dom";

import {
  getBottomTabProductNavItems,
  getTopHeaderProductNavItems,
  isProductNavActive,
  type ProductNavItem,
} from "@/config/productNavigation";
import { prefetchOmniCineRoute } from "@/lib/routePrefetch";

export type ModuleNavItem = { to: string; label: string; short?: string };

export const MODULE_NAV_ITEMS: ModuleNavItem[] = getTopHeaderProductNavItems().map((item) => ({
  to: item.path,
  label: item.label,
  short: item.shortLabel,
}));

function warmRoute(path: string) {
  if (path === "/omnicine") {
    void prefetchOmniCineRoute();
  }
}

function topNavLabel(item: ProductNavItem, compact: boolean): string {
  if (compact) return item.shortLabel ?? item.label;
  return item.shortLabel && item.label.length > 18 ? item.shortLabel : item.label;
}

export default function TopModulesNav(props: { compact?: boolean }): JSX.Element {
  const compact = props.compact ?? false;
  const location = useLocation();
  const items = getTopHeaderProductNavItems();

  return (
    <nav
      aria-label="SynkTribe modules"
      className="relative flex max-w-full items-center rounded-2xl border border-black/10 bg-white/70 px-1.5 py-1.5 shadow-[0_16px_40px_-18px_rgba(15,23,42,0.28)] backdrop-blur-md dark:border-white/10 dark:bg-white/10"
    >
      <div className="flex items-center gap-1.5 overflow-x-auto overscroll-x-contain pr-1 no-scrollbar" role="tablist">
        {items.map((item) => {
          const active = isProductNavActive(location.pathname, item);

          return (
            <NavLink
              key={item.id}
              to={item.path}
              role="tab"
              onMouseEnter={() => warmRoute(item.path)}
              onFocus={() => warmRoute(item.path)}
              onPointerDown={() => warmRoute(item.path)}
              className={[
                compact
                  ? "rounded-xl px-3 py-1.5 text-[12px] font-semibold transition-colors whitespace-nowrap"
                  : "rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors whitespace-nowrap",
                "text-slate-800/90 dark:text-white/85",
                "hover:bg-black/[0.04] dark:hover:bg-white/[0.08]",
                "focus:outline-none focus-visible:ring-2 ring-offset-2 ring-black/40 dark:ring-white/40 ring-offset-white dark:ring-offset-black",
                active
                  ? "bg-[length:200%_100%] bg-[linear-gradient(90deg,#3B82F6_0%,#22D3EE_50%,#22C55E_100%)] text-white shadow-sm motion-safe:animate-[sheen_8s_linear_infinite]"
                  : "bg-transparent",
              ].join(" ")}
            >
              {topNavLabel(item, compact)}
            </NavLink>
          );
        })}
      </div>

      <style>{`
        @keyframes sheen {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </nav>
  );
}

export function MobileModulesNav(): JSX.Element {
  const location = useLocation();
  const items = getBottomTabProductNavItems();

  return (
    <nav
      aria-label="SynkTribe mobile modules"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-black/10 bg-white/80 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl dark:border-white/10 dark:bg-black/40"
    >
      <div className="mx-auto flex max-w-[720px] gap-2 overflow-x-auto px-2 py-2 no-scrollbar">
        {items.map((item) => {
          const active = isProductNavActive(location.pathname, item);

          return (
            <NavLink
              key={item.id}
              to={item.path}
              onFocus={() => warmRoute(item.path)}
              onMouseEnter={() => warmRoute(item.path)}
              className={[
                "flex h-11 min-w-[104px] items-center justify-center rounded-2xl border px-3 text-[12px] font-semibold transition whitespace-nowrap",
                active
                  ? "border-cyan-300/25 bg-cyan-400/12 text-slate-900 dark:text-white"
                  : "border-black/10 bg-white/60 text-slate-800/85 dark:border-white/10 dark:bg-white/5 dark:text-white/75",
                "focus:outline-none focus-visible:ring-2 ring-cyan-400/50",
              ].join(" ")}
              title={item.label}
            >
              {item.shortLabel ?? item.label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
